import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check } from 'lucide-react'
import * as React from 'react'
import type { FieldPathByValue, FieldValues } from 'react-hook-form'

import {
  useFieldBinding,
  type UseFieldBindingProps
} from '@/hooks/use-field-binding'
import { cn } from '@/lib/utils'
import { FieldWrapper } from '../enhanced/field-wrapper'

export type CheckboxProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPathByValue<TFieldValues, boolean> = FieldPathByValue<
    TFieldValues,
    boolean
  >
> = UseFieldBindingProps<TFieldValues, TName> & {
  label?: React.ReactNode
} & Omit<
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>,
    | 'name'
    | 'value'
    | 'checked'
    | 'onCheckedChange'
    | 'disabled'
    | 'defaultChecked'
  >

function Checkbox<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPathByValue<TFieldValues, boolean> = FieldPathByValue<
    TFieldValues,
    boolean
  >
>(props: CheckboxProps<TFieldValues, TName>) {
  const { className, label, ...bindingAndNative } = props
  const field = useFieldBinding(bindingAndNative)
  const {
    control: _control,
    name: _name,
    value: _value,
    onChange: _onChange,
    onBlur: _onBlur,
    error: _error,
    disabled: _disabled,
    ...nativeProps
  } = bindingAndNative

  return (
    <FieldWrapper
      label={label}
      error={field.error}
      orientation='horizontal'
      controlId={nativeProps.id}
      labelPlacement='end'
    >
      {({ controlId, errorId }) => (
        <CheckboxPrimitive.Root
          data-slot='checkbox'
          {...nativeProps}
          id={controlId}
          name={field.name}
          checked={field.value}
          onCheckedChange={field.onChange}
          onBlur={field.onBlur}
          disabled={field.disabled}
          aria-invalid={field.error ? true : nativeProps['aria-invalid']}
          aria-describedby={errorId ?? nativeProps['aria-describedby']}
          className={cn(
            'peer border-primary focus-visible:ring-ring data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground h-4 w-4 shrink-0 rounded-sm border shadow transition-opacity focus-visible:ring-1 focus-visible:outline-none active:opacity-60 disabled:cursor-not-allowed disabled:opacity-50',
            className
          )}
        >
          <CheckboxPrimitive.Indicator
            className={cn('flex items-center justify-center text-current')}
          >
            <Check className='h-full w-full' />
          </CheckboxPrimitive.Indicator>
        </CheckboxPrimitive.Root>
      )}
    </FieldWrapper>
  )
}

export { Checkbox }
